import { CONTENT_BLOCK_TYPES, getContentBlockConfig } from "../constants/contentBlockConstants";

/**
 * 获取内容块类型对应的业务上下文键
 * @param {string} contentBlockType - 内容块类型
 * @returns {string|null} 业务上下文中的键名
 */
export const getContextKeyForContentBlock = (contentBlockType) => {
    const contextMap = {
        [CONTENT_BLOCK_TYPES.FLIGHT_INFO]: "flights",
        [CONTENT_BLOCK_TYPES.FLIGHT_LIST]: "flights",
        [CONTENT_BLOCK_TYPES.FLIGHT_SCHEDULE]: "schedule",
        [CONTENT_BLOCK_TYPES.ATTACHMENT]: "attachments",
        [CONTENT_BLOCK_TYPES.ATTACHMENT_LIST]: "attachments",
        [CONTENT_BLOCK_TYPES.CONTACT]: "contacts",
        [CONTENT_BLOCK_TYPES.CONTACT_LIST]: "contacts",
        [CONTENT_BLOCK_TYPES.CARGO_MANIFEST]: "cargo",
        [CONTENT_BLOCK_TYPES.CARGO_LIST]: "cargo",
        [CONTENT_BLOCK_TYPES.FUEL_INFO]: "fuel",
        [CONTENT_BLOCK_TYPES.FUEL_REQUEST]: "fuel",
        [CONTENT_BLOCK_TYPES.CREW_INFO]: "crew",
        [CONTENT_BLOCK_TYPES.CREW_LIST]: "crew",
        [CONTENT_BLOCK_TYPES.AIRPORT_INFO]: "airports",
        [CONTENT_BLOCK_TYPES.DATETIME]: "datetime",
        [CONTENT_BLOCK_TYPES.SCHEDULE_PERIOD]: "schedule",
        [CONTENT_BLOCK_TYPES.SCHEDULE_FLIGHT_LIST]: "flights",
        [CONTENT_BLOCK_TYPES.SCHEDULE_FLIGHT_LIST_WITH_COMPARISON]: "scheduleFlightComparison",
        [CONTENT_BLOCK_TYPES.SCHEDULE_INFO]: "schedule",
        [CONTENT_BLOCK_TYPES.CONTENT_BLOCK]: "content",
        [CONTENT_BLOCK_TYPES.SIGNATURE]: "user",
    };
    
    const result = contextMap[contentBlockType] || null;
    console.log("🔍 [getContextKeyForContentBlock] 映射结果:", {
        contentBlockType,
        mappedKey: result,
        availableTypes: Object.keys(contextMap)
    });
    
    return result;
};

/**
 * 解析模板内容，提取内容块信息，并用唯一令牌替换它们
 * @param {string} templateContent - 包含内容块的HTML模板内容
 * @returns {{tokenizedTemplate: string, blocks: Array}} 解析结果
 */
export const parseTemplateContentBlocks = (templateContent) => {
    if (!templateContent || typeof templateContent !== "string") {
        return { tokenizedTemplate: "", blocks: [] };
    }

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = templateContent;

    const contentBlockElements = tempDiv.querySelectorAll("[data-content-block-type]");
    const blocks = [];

    contentBlockElements.forEach((element, index) => {
        const contentBlockType = element.getAttribute("data-content-block-type");
        const contentBlockId = element.getAttribute("data-content-block-id");
        const contentBlockLabel = element.getAttribute("data-content-block-label");
        const dataKey = element.getAttribute("data-key");

        // 新增：解析显示模式属性
        const displayMode = element.getAttribute("data-display-mode");
        // 根据标签类型和显示模式属性判断是否为行内模式
        const isInline = displayMode === "inline" || element.tagName.toLowerCase() === "span";

        const uniqueToken = `__CONTENT_BLOCK_TOKEN_${index}__`;

        if (contentBlockType) {
            blocks.push({
                id: contentBlockId,
                type: contentBlockType,
                label: contentBlockLabel || "点击右侧插入内容",
                dataKey: dataKey,
                token: uniqueToken, // 存储令牌用于后续替换
                index,
                visible: true,
                // 新增：添加显示模式信息
                displayMode: isInline ? "inline" : "block",
                isInline: isInline,
            });
        }

        // 用文本节点令牌替换原始的DOM元素
        element.replaceWith(document.createTextNode(uniqueToken));
    });

    return {
        tokenizedTemplate: tempDiv.innerHTML,
        blocks,
    };
};

/**
 * 根据内容块信息生成SidePanel配置
 * 注意：这里不再基于业务上下文数据生成sections，而是基于模板中的内容块
 * @param {Array} contentBlocks - 内容块信息数组
 * @param {Object} businessContext - 业务上下文数据
 * @returns {Array} SidePanel sections 配置
 */
export const generateSidePanelSections = (contentBlocks, businessContext) => {
    if (!contentBlocks || !Array.isArray(contentBlocks)) {
        return [];
    }

    // 按类型分组内容块
    const groupedBlocks = contentBlocks.reduce((groups, block) => {
        if (!groups[block.type]) {
            groups[block.type] = [];
        }
        groups[block.type].push(block);
        return groups;
    }, {});

    const sections = [];

    // 为每个类型创建一个section
    Object.entries(groupedBlocks).forEach(([type, blocks]) => {
        const config = getContentBlockConfig(type);

        if (config) {
            const section = createContentBlockSection(type, blocks, businessContext, config);

            if (section) {
                sections.push(section);
            }
        }
    });

    return sections;
};

/**
 * 为内容块类型创建SidePanel section
 * @param {string} contentBlockType - 内容块类型
 * @param {Array} contentBlocks - 该类型的所有内容块
 * @param {Object} businessContext - 业务上下文数据
 * @param {Object} config - 显示配置
 * @returns {Object|null} SidePanel section 配置
 */
const createContentBlockSection = (contentBlockType, contentBlocks, businessContext, config) => {
    const contextKey = getContextKeyForContentBlock(contentBlockType);
    const contextData = contextKey ? businessContext[contextKey] : null;

    return {
        id: contentBlockType,
        title: config.label,
        icon: config.icon,
        type: "contentBlocks", // 标识这是内容块类型的section
        contentBlocks: contentBlocks.map((block) => ({
            ...block,
            config,
            contextData: getDataForContentBlock(block, contextData),
            displayLabel: generateDisplayLabel(block, contextData),
        })),
    };
};

/**
 * 为内容块获取对应的数据
 * @param {Object} contentBlock - 内容块信息
 * @param {*} contextData - 上下文数据
 * @returns {*} 对应的数据
 */
export const getDataForContentBlock = (contentBlock, contextData) => {
    if (!contextData) return null;

    // 如果指定了dataKey，尝试获取特定数据
    if (contentBlock.dataKey) {
        if (Array.isArray(contextData)) {
            // 如果是数组，尝试通过索引或ID获取
            const index = parseInt(contentBlock.dataKey);
            if (!isNaN(index) && index < contextData.length) {
                return contextData[index];
            }
            // 尝试通过ID匹配
            return contextData.find((item) => item.id === contentBlock.dataKey);
        } else if (typeof contextData === "object") {
            // 如果是对象，直接获取属性
            return contextData[contentBlock.dataKey];
        }
    }

    // 如果没有指定dataKey，返回整个数据集
    return contextData;
};

/**
 * 生成内容块的显示标签
 * @param {Object} contentBlock - 内容块信息
 * @param {*} contextData - 上下文数据
 * @returns {string} 显示标签
 */
const generateDisplayLabel = (contentBlock, contextData) => {
    if (contentBlock.label && contentBlock.label !== "点击右侧插入内容") {
        return contentBlock.label;
    }

    // 根据内容块类型和数据生成智能标签
    const data = getDataForContentBlock(contentBlock, contextData);

    switch (contentBlock.type) {
        case CONTENT_BLOCK_TYPES.FLIGHT_INFO:
            if (data && data.flight_number) {
                return `航班信息 - ${data.flight_number}`;
            }
            return "航班信息";

        case CONTENT_BLOCK_TYPES.FLIGHT_LIST:
            if (Array.isArray(data)) {
                return `航班列表 (${data.length}个航班)`;
            }
            return "航班列表";

        case CONTENT_BLOCK_TYPES.CONTACT:
            if (data && data.name) {
                return `联系人 - ${data.name}`;
            }
            return "联系人信息";

        case CONTENT_BLOCK_TYPES.ATTACHMENT:
            if (data && data.filename) {
                return `附件 - ${data.filename}`;
            }
            return "附件";

        case CONTENT_BLOCK_TYPES.SCHEDULE_FLIGHT_LIST:
            if (Array.isArray(data)) {
                return `航班计划列表 (${data.length}个航班)`;
            }
            return "航班计划列表";

        case CONTENT_BLOCK_TYPES.SCHEDULE_FLIGHT_LIST_WITH_COMPARISON:
            console.log("🔍 [generateDisplayLabel] SCHEDULE_FLIGHT_LIST_WITH_COMPARISON data:", data);
            if (data && data.summary) {
                const { totalFlights = 0 } = data.summary;
                return `计划航班列表（含对比） (${totalFlights}个航班)`;
            } else if (data && data.flights) {
                return `计划航班列表（含对比） (${data.flights.length}个航班)`;
            }
            return "计划航班列表（含对比）";

        case CONTENT_BLOCK_TYPES.SCHEDULE_INFO:
            if (data && data.name) {
                return `计划信息 - ${data.name}`;
            }
            return "计划基本信息";

        default:
            return contentBlock.label || `${contentBlock.type} 内容块`;
    }
};

/**
 * 获取模板中所有内容块的类型
 * @param {string} templateContent - 包含内容块的HTML模板内容
 * @returns {Array<string>} 内容块类型数组
 */
export const getContentBlockTypes = (templateContent) => {
    const contentBlockElements = parseTemplateContentBlocks(templateContent);
    return [...new Set(contentBlockElements.blocks.map((cb) => cb.type))];
};

/**
 * 验证业务上下文是否包含模板所需的所有内容块数据
 * @param {Array<string>} contentBlockTypes - 模板中要求的内容块类型列表
 * @param {Object} businessContext - 业务上下文数据
 * @returns {{isValid: boolean, missing: Array<string>}} 验证结果
 */
export const validateBusinessContext = (contentBlockTypes, businessContext) => {
    const missing = [];
    if (!businessContext) {
        return { isValid: false, missing: ["businessContext"] };
    }

    for (const type of contentBlockTypes) {
        const contextKey = getContextKeyForContentBlock(type);
        if (contextKey && !businessContext.hasOwnProperty(contextKey)) {
            missing.push(contextKey);
        }
    }

    return {
        isValid: missing.length === 0,
        missing,
    };
};
